import React, { useEffect, useState } from 'react'
import { all_routes } from '../../../router/all_routes'
import { Link } from 'react-router-dom'
import Table from "../../shared/dataTable/index";
import CommonSelect from '../../../core/common/commonSelect';
import departmentService from '../../../services/department/department.service';
import {DepartmentDto, DepartmentCreateDTO, DepartmentUpdateDTO, DepartmentStatus, LeaveApprovalHierarchy } from '../../../types/department.types';
import { Popconfirm } from 'antd';
import { createPdf, exportToExcel, normalizeTurkishChars } from '../../../utils/exportUtils';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import { handleApiError } from '../../../utils/errorHandler';
import dayjs from 'dayjs';
import FooterSection from '../../shared/components/footerSection';
import DepartmentModal from '../../shared/modals/DepartmentModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { PermissionType, User } from '../../../types/user.types';
import LeaveHierarchyModal from '../department/LeaveHierarchyModal';

const Department = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const {getDepartments,createDepartment,updateDepartment,deleteDepartment, getDepartmentHierarchy} = departmentService
  const {t} = useTranslation()

  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [hierarchies, setHierarchies] = useState<Record<number, LeaveApprovalHierarchy | null>>({});
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Kopyalamada modal'a verilecek başlangıç değerleri. State'te tutuluyor çünkü modal
  // initialValues referansı her değiştiğinde formu yeniden dolduruyor.
  const [duplicateValues, setDuplicateValues] = useState<DepartmentCreateDTO | undefined>();

  // State for the new hierarchy modal
  const [hierarchyModalVisible, setHierarchyModalVisible] = useState(false);
  const [selectedDepartmentForHierarchy, setSelectedDepartmentForHierarchy] = useState<DepartmentDto | null>(null);

  
  const fetchDepartments = async () => {
    setLoading(true);
    setTimeout(async () => {
      try {
        const result = await getDepartments();
        setDepartments(result);
        
        // Fetch hierarchies for each department
        const hierarchyPromises = result.map(dept => 
          getDepartmentHierarchy(dept.id)
            .then(hierarchy => ({ deptId: dept.id, hierarchy }))
            .catch(() => ({ deptId: dept.id, hierarchy: null })) // Handle errors, e.g., 404
        );
        
        const hierarchyResults = await Promise.all(hierarchyPromises);
        const hierarchyMap = hierarchyResults.reduce((acc, { deptId, hierarchy }) => {
          acc[deptId] = hierarchy;
          return acc;
        }, {} as Record<number, LeaveApprovalHierarchy | null>);
        
        setHierarchies(hierarchyMap);

      } catch (err: any) {
        handleApiError(err,t);
      } finally {
        setLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const handleSave = async (values: DepartmentCreateDTO | DepartmentUpdateDTO) => {
    try {
      if (selectedDepartment) {
        const updatedValues: DepartmentDto = {
          ...selectedDepartment,
          ...values,
        };
        await updateDepartment(selectedDepartment.id, updatedValues);
        message.success(t('departments.departmentUpdateSuccess'));
      } else {
        const newDepartment: DepartmentDto = {
          ...values,
          id: 0, // Geçici ID, backend'de oluşturulacak
          isActive: true, // Varsayılan olarak aktif
          status: values.status ?? DepartmentStatus.ACTIVE, // Form'daki seçim korunur, boşsa varsayılana düşer
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          employeeCount: 0,
          maxMealBreakDuration: values.maxMealBreakDuration || undefined, // Bu satırı ekledim
        };
        await createDepartment(newDepartment);
        message.success(t('departments.departmentSuccess'));
      }
      fetchDepartments();
      handleClear();
      
      // 🔥 Sidebar'a departmanların güncellendiğini bildir
      window.dispatchEvent(new CustomEvent('departmentsUpdated'));
    } catch (err: any) {
      handleApiError(err,t);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteDepartment(id);
      message.success(t('departments.departmentDeleteSuccess'));
      fetchDepartments();
      handleClear();
      
      // 🔥 Sidebar'a departmanların güncellendiğini bildir
      window.dispatchEvent(new CustomEvent('departmentsUpdated'));
    } catch (err: any) {
      handleApiError(err,t);
    }
  };

  const handleClear = () => {
    setSelectedDepartment(null);
    setDuplicateValues(undefined);
    setModalVisible(false);
    setIsEditMode(false);
  };

  const handleAddDepartment = () => {
    setSelectedDepartment(null);
    setDuplicateValues(undefined);
    setIsEditMode(false);
    setModalVisible(true);
  };

  const handleEditDepartment = (department: DepartmentDto) => {
    setSelectedDepartment(department);
    setDuplicateValues(undefined);
    setIsEditMode(true);
    setModalVisible(true);
  };

  // Mevcut departmanın çalışma saatleri ve mola ayarlarıyla yeni departman formunu açar.
  // Aynı isimde departman oluşturulamadığı için isme "(Kopya)" ekleniyor.
  const handleDuplicateDepartment = (department: DepartmentDto) => {
    setSelectedDepartment(null);
    setDuplicateValues({
      name: `${department.name} (Kopya)`,
      status: department.status,
      fixedShiftStartTime: department.fixedShiftStartTime,
      fixedShiftEndTime: department.fixedShiftEndTime,
      fixedShiftDays: department.fixedShiftDays,
      lateTolerance: department.lateTolerance,
      lateTolerationMinutes: department.lateTolerationMinutes,
      defaultBreakDuration: department.defaultBreakDuration,
      maxBreakDuration: department.maxBreakDuration,
      maxMealBreakDuration: department.maxMealBreakDuration,
      overtimeRequestEnabled: department.overtimeRequestEnabled,
      fieldModeApprovalMode: department.fieldModeApprovalMode,
      activityApprovalMode: department.activityApprovalMode,
      activityApprovalTypes: department.activityApprovalTypes,
    });
    setIsEditMode(false);
    setModalVisible(true);
  };

  // Handlers for the hierarchy modal
  const handleHierarchyClick = (department: DepartmentDto) => {
    setSelectedDepartmentForHierarchy(department);
    setHierarchyModalVisible(true); 
  };
  
  const handleHierarchyModalClose = () => {
    setHierarchyModalVisible(false);
    setSelectedDepartmentForHierarchy(null);
  };
  
  const handleHierarchyUpdate = () => {
    fetchDepartments(); // Refetch all data to get updated hierarchy info
  }

  const columns = [
    {
      title: t('departments.department'),
      dataIndex: "name",
      render: (text: string, record: any) => (
        <Link 
        to={`${all_routes.employeeList}?department=${record.id}`}
        >
          {text}
        </Link>
      ),
      sorter: (a: any, b: any) => a.Department.length - b.Department.length,
    },
    {
      title: t('departments.numberOfEmployees'),
      dataIndex: "employeeCount",
      sorter: (a: any, b: any) => a.employeeCount - b.employeeCount,
    },
    ...(user?.permissions?.includes(PermissionType.MANAGE_LEAVE_HIERARCHY) ? [{
      title: t('departments.leaveHierarchy', 'İzin Onay Hiyerarşisi'),
      dataIndex: 'hierarchy',
      render: (_: any, record: DepartmentDto) => {
        const hierarchy = hierarchies[record.id];
        const hasHierarchy = hierarchy && hierarchy.steps && hierarchy.steps.length > 0;
        return (
          <button
            className={`btn btn-sm ${hasHierarchy ? 'bg-outline-dinox-purple bg-white' : 'btn-primary'}`}
            onClick={() => handleHierarchyClick(record)}
          >
            {hasHierarchy ? t('departments.editHierarchy', 'Hiyerarşi Düzenle') : t('departments.addHierarchy', 'Hiyerarşi Ekle')}
          </button>
        );
      },
    }] : []),
    {
      title: t('departments.status'),
      dataIndex: "isActive",
      render: (_:any, record: DepartmentDto) => (
        <span className={`badge ${record.status === DepartmentStatus.ACTIVE ? 'badge-success' : 'badge-danger'} d-inline-flex align-items-center badge-xs`}>
          <i className="ti ti-point-filled me-1" />
          {record.status === DepartmentStatus.ACTIVE?t('departments.active'):t('departments.inactive')}
        </span>

      ),
      sorter: (a: any, b: any) => a.status.length - b.status.length,
    },
    
    {
      title: "",
      dataIndex: "actions",
      render: (_: any, record: any) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleEditDepartment(record)}
          >
            <i className="ti ti-edit" />
          </button>
          {user?.permissions?.includes(PermissionType.EDIT_DEPARTMENTS) && (
          <button
            className="btn btn-sm btn-white border"
            title="Kopyala"
            onClick={() => handleDuplicateDepartment(record)}
          >
            <i className="ti ti-copy" />
          </button>
          )}
          {user?.permissions?.includes(PermissionType.EDIT_DEPARTMENTS) && (
          <Popconfirm
            title={t('departments.departmentDeleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('departments.yes')}
            cancelText={t('departments.no')}
          >
            <button
              className="btn btn-sm btn-danger"
            >
              <i className="ti ti-trash" />
            </button>
          </Popconfirm>
          )}
        </div>

      ),
    },
  ];

  const getFilteredDepartments = () => {
    if (statusFilter === 'all') {
      return departments;
    }
    return departments.filter(dep => 
      statusFilter === 'active' ? dep.status === DepartmentStatus.ACTIVE : dep.status === DepartmentStatus.INACTIVE
    );
  };

  const statusOptions = [
    { value: 'all', label: t('departments.allDepartments') },
    { value: 'active', label: t('departments.activeDepartments') },
    { value: 'inactive', label: t('departments.inactiveDepartments') }
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">{t('departments.pageTitle')}</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">{t('departments.breadcrumb.employee')}</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {t('departments.breadcrumb.departments')}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap  ">
            
                <button
                  onClick={handleAddDepartment}
                  className="btn btn-sm btn-primary d-inline-flex align-items-center me-2"
                >
                    <i className="ti ti-plus me-1" />
                    {t('departments.addDepartment')}
                </button>
                {user?.permissions?.includes(PermissionType.EXPORT) && (
              <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    {t('common.buttons.export')}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-2 d-flex align-items-center py-2"
                        onClick={() => {
                          // Departmanlar için sütun tanımları
                          type DepartmentDataType = {
                            name: string;
                            employeeCount: number;
                            status: string;
                          };

                          const columnsForPdf = [
                            { header: 'Departman Adi', property: 'name' as keyof DepartmentDataType, width: 100 },
                            { header: 'Calisan Sayisi', property: 'employeeCount' as keyof DepartmentDataType, width: 100 },
                            { header: 'Durum', property: 'status' as keyof DepartmentDataType, width: 80 }
                          ];

                          // PDF için formatlanmış veri
                          const formattedData = departments?.map(dept => ({
                            name: normalizeTurkishChars(dept.name) || '-',
                            employeeCount: dept.employeeCount || 0,
                            status: dept.status === DepartmentStatus.ACTIVE ? normalizeTurkishChars(t('departments.active')) : normalizeTurkishChars(t('departments.inactive'))
                          }));

                          // Açıklama
                          let description = normalizeTurkishChars('Departman listesi');
                          if (statusFilter !== 'all') {
                            description += statusFilter === 'active' 
                              ? ' - Yalnizca aktif departmanlar' 
                              : ' - Yalnizca pasif departmanlar';
                          }

                          // createPdf fonksiyonunu çağır
                          createPdf(
                            formattedData, 
                            {
                              title: normalizeTurkishChars('Departman Listesi'),
                              description: description,
                              headerColor: '#2c3e50', // Koyu lacivert
                              accentColor: '#3498db', // Mavi
                              columns: columnsForPdf,
                              showStatistics: false, // İstatistik kutuları gösterme
                              footer: {
                                text: `© ${new Date().getFullYear()} ${normalizeTurkishChars('DINOX')} - ${normalizeTurkishChars('Tum Haklari Saklidir')}`,
                                showPageNumbers: true
                              }
                            }
                          );
                        }}
                      >
                        <i className="ti ti-file-type-pdf me-2 text-danger" />
                        {t('common.buttons.pdf')}
                      </Link>
                    </li>
                    <li className="mt-1">
                      <Link
                        to="#"
                        className="dropdown-item rounded-2 d-flex align-items-center py-2"
                        onClick={() => {
                          // Excel için sütun tanımları
                          type DepartmentExcelType = {
                            name: string;
                            employeeCount: number;
                            status: string;
                            createdAt?: string;
                          };

                          const columnsForExcel = [
                            { header: 'Departman Adı', property: 'name' as keyof DepartmentExcelType, width: 30 },
                            { header: 'Çalışan Sayısı', property: 'employeeCount' as keyof DepartmentExcelType, width: 20 },
                            { header: 'Durum', property: 'status' as keyof DepartmentExcelType, width: 20 },
                            { header: 'Oluşturulma Tarihi', property: 'createdAt' as keyof DepartmentExcelType, width: 25 }
                          ];

                          // Excel için formatlanmış veri
                          const formattedData = departments?.map(dept => ({
                            name: dept.name || '-',
                            employeeCount: dept.employeeCount || 0,
                            status: dept.status === DepartmentStatus.ACTIVE ? t('departments.active') : t('departments.inactive'),
                            createdAt: dept.createdAt ? dayjs(dept.createdAt).format('DD.MM.YYYY') : '-'
                          }));

                          // Excel dosyasını oluştur
                          exportToExcel(formattedData, 'Departman Listesi', columnsForExcel);
                        }}
                      >
                        <i className="ti ti-file-type-xls me-2 text-success" />
                        {t('common.buttons.excel')}
                      </Link>
                    </li>
                  </ul>
                </div>
                )}
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>{t('departments.departmentList')}</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="me-3">
                  <CommonSelect
                    options={statusOptions}
                    value={statusOptions.find(option => option.value === statusFilter)}
                    onChange={(selected: any) => setStatusFilter(selected.value.toString())}
                    className="select"
                  />
                </div>
              </div>
            </div>
            <div className="card-body p-0" data-tour="departments-list">
              {loading ? (
                <LoadingSpinner centered={true} />
              ) : (
                <Table dataSource={getFilteredDepartments()} columns={columns} Selection={false} />
              )}
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <FooterSection />
        </div>
      </div>
      {/* /Page Wrapper */}
      
      {/* Department Modal */}
      <DepartmentModal
        visible={modalVisible}
        onCancel={handleClear}
        onSubmit={handleSave}
        initialValues={selectedDepartment || duplicateValues}
        isEdit={isEditMode}
        loading={loading}
        currentUser={user as User}
        onOpenHierarchyModal={handleHierarchyClick}
      />

      {/* Leave Hierarchy Modal */}
      {selectedDepartmentForHierarchy && (
        <LeaveHierarchyModal
          visible={hierarchyModalVisible}
          onClose={handleHierarchyModalClose}
          department={selectedDepartmentForHierarchy}
          onHierarchyUpdate={handleHierarchyUpdate}
        />
      )}
    </>
  )
}

export default Department
